/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { RefObject, useEffect } from "react";

export default function useOnClickOutside(
  ref: RefObject<HTMLElement>,
  handler: (event: MouseEvent) => void
): void {
  useEffect(
    () => {
      // Ignore the click that opened the modal/panel that mounted this
      // hook — otherwise that same click can bubble up to the document
      // listener and immediately close what just opened. Both timestamps
      // share the document's navigation-start origin, so comparing them
      // directly is safe.
      const attachedAt = performance.now();
      const listener = (event: MouseEvent): void => {
        if (event.timeStamp < attachedAt) {
          return;
        }
        // Do nothing if clicking ref's element or descendent elements
        if (
          !ref.current ||
          ref.current.contains(event.target as HTMLElement) ||
          !document.contains(event.target as HTMLElement) // also do nothing if the element is stale, as this indicates that something else has responded to the event.
        ) {
          return;
        }
        handler(event);
      };
      document.addEventListener("click", listener);
      document.addEventListener("touchstart", listener);
      return (): void => {
        document.removeEventListener("click", listener);
        document.removeEventListener("touchstart", listener);
      };
    },
    // Add ref and handler to effect dependencies
    // It's worth noting that because passed in handler is a new ...
    // ... function on every render that will cause this effect ...
    // ... callback/cleanup to run every render. It's not a big deal ...
    // ... but to optimize you can wrap handler in useCallback before ...
    // ... passing it into this hook.
    [ref, handler]
  );
}
